// Copyright 2026 Bitech.id. Licensed under the MIT License.

/**
 * Biwave Stream (session replay) config, passed via `init({ stream: {...} })`.
 * `platform`/`capture_mode` are fixed to 'web'/'dom' for @biwave/browser —
 * 'wireframe'/'screenshot' are mobile-SDK capture modes, out of scope here.
 */
export interface StreamOptions {
  enabled?: boolean;
  /** 0-1, probability a session is actively recorded+uploaded. Default 0.1. */
  sessionSampleRate?: number;
  /** 0-1, probability a non-sampled session is kept in a ring buffer for error-triggered promotion. Default 1.0. */
  errorSampleRate?: number;
  /** Ring buffer retention window, in seconds. Default 60. */
  bufferSeconds?: number;
  /** Default false — text is recorded as-is (LogRocket-style; only passwords are masked). Set true to mask all text nodes (per-element opt-out via the 'biwave-unmask' class). */
  maskAllText?: boolean;
  /** Default true — captures console/network/click/navigation as discrete, countable session events (LogRocket-style timeline). */
  captureEvents?: boolean;
}

export interface ResolvedStreamOptions {
  sessionSampleRate: number;
  errorSampleRate: number;
  bufferSeconds: number;
  maskAllText: boolean;
  captureEvents: boolean;
}

export const DEFAULT_STREAM_OPTIONS: ResolvedStreamOptions = {
  sessionSampleRate: 0.1,
  errorSampleRate: 1.0,
  bufferSeconds: 60,
  maskAllText: false,
  captureEvents: true,
};

/** Returned by POST /api/stream/sessions — must be obeyed, never hardcoded. */
export interface StreamLimits {
  max_chunk_bytes: number;
  max_session_duration_ms: number;
  max_chunks: number;
}

export type StreamEndReason = 'normal' | 'crash' | 'user_optout';

export type StreamMode = 'recording' | 'buffered';

export interface StreamSessionState {
  session_id: string;
  mode: StreamMode;
  started_at: number;
  /** Next seq to assign — monotonic, never reused for different content. */
  next_seq: number;
  /** Set once the init response is known; recording mode assumes defaults until then. */
  limits: StreamLimits | null;
  /** True once POST /api/stream/sessions has been called (successfully or not — init is fire-and-forget-ish, chunks don't wait for it). */
  init_sent: boolean;
}

export const DEFAULT_LIMITS: StreamLimits = {
  max_chunk_bytes: 1_048_576,
  max_session_duration_ms: 3_600_000,
  max_chunks: 2000,
};
